import { useMemo } from 'react';

import { Icon, tv } from '@eluve/components';
import { computeAudioLevel } from '@eluve/mic-utils';

export const microphoneLevelVariant = tv({
  slots: {
    base: 'flex items-center rounded-lg bg-bg-tertiary',
    iconWrapper: 'flex items-center justify-center text-text-primary',
    levelBarsWrapper: 'flex gap-2',
    levelBar:
      'h-4.5 w-2 rounded-full transition-colors duration-100 ease-in-out',
  },
  variants: {
    barStatus: {
      active: {
        levelBar: 'bg-bg-accent-strong-primary',
      },
      inactive: {
        levelBar: 'bg-border-secondary',
      },
    },
    variant: {
      default: {
        base: 'w-max flex-col gap-12 p-8 pt-10',
        iconWrapper: 'rounded-full bg-bg-strong-secondary p-4 text-text-strong',
      },
      compact: {
        base: 'w-full justify-between gap-3 p-3',
        levelBar: 'h-4 w-1.5 rounded-full',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export interface MicrophoneLevelVisualizationProps {
  waveformData?: number[];
  variant?: 'default' | 'compact';
  className?: string;
}

export const LEVEL_BAR_COUNT = {
  compact: 10,
  default: 20,
};

/**
 * Pure presentation component that displays audio level visualization from waveform data.
 * Accepts frequency data as a prop instead of directly analyzing a MediaStream.
 * Used by MicrophoneLevel for stream-based analysis, and directly in extension contexts
 * where the stream is in a different context (e.g., offscreen document).
 */
export const MicrophoneLevelVisualization: React.FC<
  MicrophoneLevelVisualizationProps
> = ({ waveformData, variant = 'default', className }) => {
  const { base, levelBarsWrapper, levelBar, iconWrapper } =
    microphoneLevelVariant({ variant });

  const barCount = useMemo(() => {
    if (!waveformData || waveformData.length === 0) {
      return 0;
    }

    return Math.round(
      computeAudioLevel(waveformData) * LEVEL_BAR_COUNT[variant],
    );
  }, [waveformData, variant]);

  return (
    <div
      data-accessibility-label="microphone-level"
      className={base({ className })}
    >
      <div className={iconWrapper()}>
        <Icon name="Mic" size={variant === 'compact' ? 'xs' : 'md'} />
      </div>
      <div className={levelBarsWrapper()}>
        {Array(LEVEL_BAR_COUNT[variant])
          .fill(0)
          .map((_, i) => (
            <div
              key={i}
              className={levelBar({
                barStatus: i + 1 < barCount ? 'active' : 'inactive',
              })}
            />
          ))}
      </div>
    </div>
  );
};
